import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_CONFIG } from "../config/api";
import DynamicForm from "./DynamicForm";

interface BrandingInfo {
  branding: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    logo: string;
    logoUrl: string;
    theme: string;
    fontFamily: string;
    customCSS: string;
    footerText: string;
    contactInfo: {
      phone: string;
      email: string;
      address: string;
    };
  };
  patientInfo: {
    patient_id: string;
    patient_date_of_birth: string;
    location_id: string;
  };
}

interface ConsentStatus {
  hipaaConsentSubmitted: boolean;
  financialConsentSubmitted: boolean;
  patientIntakeSubmitted: boolean;
  isUsed: boolean;
}

interface FormSelectionProps {
  token: string;
  jwtToken: string;
  brandingInfo: BrandingInfo | null;
}

const FormSelection: React.FC<FormSelectionProps> = ({
  token,
  jwtToken,
  brandingInfo,
}) => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedForm, setSelectedForm] = useState<string | null>(null);

  useEffect(() => {
    fetchConsentStatus();
  }, [jwtToken]);

  const fetchConsentStatus = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CONSENT_STATUS}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );
      setConsentStatus(response.data.consentStatus);
    } catch (error) {
      console.error("Error fetching consent status:", error);
      setError("Failed to load form status. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSelection = (formType: string) => {
    setSelectedForm(formType);
  };

  const handleFormComplete = () => {
    // Refresh consent status after form completion
    fetchConsentStatus();
    setSelectedForm(null);
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div>Loading form status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "Arial, sans-serif",
          color: "#d32f2f",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <h2>Error</h2>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              backgroundColor: "#2c5aa0",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render specific form if selected
  if (selectedForm) {
    // Map form selection to API form types
    const formTypeMap: Record<string, string> = {
      intake: "intake_form",
      hipaa: "hipaa_consent",
      financial: "financial_consent",
    };

    return (
      <DynamicForm
        token={token}
        formType={formTypeMap[selectedForm] || selectedForm}
        jwtToken={jwtToken}
        brandingInfo={brandingInfo}
        onComplete={handleFormComplete}
      />
    );
  }

  const primaryColor = brandingInfo?.branding?.primaryColor || "#2c5aa0";
  const secondaryColor = brandingInfo?.branding?.secondaryColor || "#f8f9fa";
  const fontFamily = brandingInfo?.branding?.fontFamily || "Inter, sans-serif";

  // Check if all forms are completed
  const allFormsCompleted =
    consentStatus &&
    consentStatus.hipaaConsentSubmitted &&
    consentStatus.financialConsentSubmitted &&
    consentStatus.patientIntakeSubmitted;

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: secondaryColor,
        fontFamily: fontFamily,
      }}
    >
      {/* Header */}
      <header
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
          color: "white",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        {brandingInfo?.branding?.logoUrl && (
          <img
            src={brandingInfo.branding.logoUrl}
            alt="Logo"
            style={{
              height: "60px",
              marginBottom: "1rem",
              borderRadius: "8px",
            }}
          />
        )}
        <h1 style={{ margin: 0, fontSize: "2rem" }}>Patient Forms</h1>
        <p style={{ margin: "0.5rem 0 0 0", opacity: 0.9 }}>
          Please complete the required forms below
        </p>
      </header>

      {/* Main Content */}
      <main
        style={{
          maxWidth: "800px",
          margin: "3rem auto",
          padding: "0 2rem",
        }}
      >
        {allFormsCompleted ? (
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "3rem",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "4rem",
                marginBottom: "1rem",
              }}
            >
              ✅
            </div>
            <h2
              style={{
                color: "#28a745",
                marginBottom: "1rem",
              }}
            >
              All Forms Completed!
            </h2>
            <p
              style={{
                color: "#666",
                marginBottom: "2rem",
                lineHeight: "1.5",
              }}
            >
              Thank you for completing all required forms. Your information has
              been submitted successfully.
            </p>
            <div
              style={{
                backgroundColor: "#f8f9fa",
                padding: "1rem",
                borderRadius: "8px",
                marginTop: "2rem",
              }}
            >
              <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>
                If you need to make any changes, please contact our office.
              </p>
            </div>
          </div>
        ) : (
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "2rem",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <h2
              style={{
                color: primaryColor,
                marginBottom: "1rem",
                textAlign: "center",
              }}
            >
              Required Forms
            </h2>

            <p
              style={{
                textAlign: "center",
                color: "#666",
                marginBottom: "2rem",
                lineHeight: "1.5",
              }}
            >
              Please complete the following forms to proceed with your
              appointment.
            </p>

            <div
              style={{
                display: "grid",
                gap: "1rem",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              }}
            >
              {/* HIPAA Consent Form */}
              <div
                style={{
                  border: "2px solid #e0e0e0",
                  borderRadius: "12px",
                  padding: "1.5rem",
                  textAlign: "center",
                  backgroundColor: consentStatus?.hipaaConsentSubmitted
                    ? "#e8f5e8"
                    : "white",
                  borderColor: consentStatus?.hipaaConsentSubmitted
                    ? "#28a745"
                    : "#e0e0e0",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>
                  {consentStatus?.hipaaConsentSubmitted ? "✅" : "🔒"}
                </div>
                <h3
                  style={{
                    color: primaryColor,
                    marginBottom: "0.5rem",
                  }}
                >
                  HIPAA Consent
                </h3>
                <p
                  style={{
                    color: "#666",
                    fontSize: "0.9rem",
                    marginBottom: "1rem",
                  }}
                >
                  Privacy authorization and health information consent
                </p>
                {consentStatus?.hipaaConsentSubmitted ? (
                  <div
                    style={{
                      color: "#28a745",
                      fontWeight: "500",
                    }}
                  >
                    ✓ Completed
                  </div>
                ) : (
                  <button
                    onClick={() => handleFormSelection("hipaa")}
                    style={{
                      backgroundColor: primaryColor,
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "0.75rem 1.5rem",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "opacity 0.3s ease",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.opacity = "0.9")}
                    onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
                  >
                    Complete Form
                  </button>
                )}
              </div>

              {/* Financial Consent Form */}
              <div
                style={{
                  border: "2px solid #e0e0e0",
                  borderRadius: "12px",
                  padding: "1.5rem",
                  textAlign: "center",
                  backgroundColor: consentStatus?.financialConsentSubmitted
                    ? "#e8f5e8"
                    : "white",
                  borderColor: consentStatus?.financialConsentSubmitted
                    ? "#28a745"
                    : "#e0e0e0",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>
                  {consentStatus?.financialConsentSubmitted ? "✅" : "💳"}
                </div>
                <h3
                  style={{
                    color: primaryColor,
                    marginBottom: "0.5rem",
                  }}
                >
                  Financial Consent
                </h3>
                <p
                  style={{
                    color: "#666",
                    fontSize: "0.9rem",
                    marginBottom: "1rem",
                  }}
                >
                  Billing authorization and payment consent
                </p>
                {consentStatus?.financialConsentSubmitted ? (
                  <div
                    style={{
                      color: "#28a745",
                      fontWeight: "500",
                    }}
                  >
                    ✓ Completed
                  </div>
                ) : (
                  <button
                    onClick={() => handleFormSelection("financial")}
                    style={{
                      backgroundColor: primaryColor,
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "0.75rem 1.5rem",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "opacity 0.3s ease",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.opacity = "0.9")}
                    onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
                  >
                    Complete Form
                  </button>
                )}
              </div>

              {/* Patient Intake Form */}
              <div
                style={{
                  border: "2px solid #e0e0e0",
                  borderRadius: "12px",
                  padding: "1.5rem",
                  textAlign: "center",
                  backgroundColor: consentStatus?.patientIntakeSubmitted
                    ? "#e8f5e8"
                    : "white",
                  borderColor: consentStatus?.patientIntakeSubmitted
                    ? "#28a745"
                    : "#e0e0e0",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>
                  {consentStatus?.patientIntakeSubmitted ? "✅" : "📋"}
                </div>
                <h3
                  style={{
                    color: primaryColor,
                    marginBottom: "0.5rem",
                  }}
                >
                  Patient Intake
                </h3>
                <p
                  style={{
                    color: "#666",
                    fontSize: "0.9rem",
                    marginBottom: "1rem",
                  }}
                >
                  Medical history and intake information
                </p>
                {consentStatus?.patientIntakeSubmitted ? (
                  <div
                    style={{
                      color: "#28a745",
                      fontWeight: "500",
                    }}
                  >
                    ✓ Completed
                  </div>
                ) : (
                  <button
                    onClick={() => handleFormSelection("intake")}
                    style={{
                      backgroundColor: primaryColor,
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      padding: "0.75rem 1.5rem",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "opacity 0.3s ease",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.opacity = "0.9")}
                    onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
                  >
                    Complete Form
                  </button>
                )}
              </div>
            </div>

            {/* Progress Indicator */}
            <div
              style={{
                marginTop: "2rem",
                padding: "1rem",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <span style={{ fontWeight: "500", color: "#333" }}>
                  Progress
                </span>
                <span style={{ color: "#666", fontSize: "0.9rem" }}>
                  {
                    [
                      consentStatus?.hipaaConsentSubmitted,
                      consentStatus?.financialConsentSubmitted,
                      consentStatus?.patientIntakeSubmitted,
                    ].filter(Boolean).length
                  }{" "}
                  of 3 completed
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "8px",
                  backgroundColor: "#e0e0e0",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${
                      ([
                        consentStatus?.hipaaConsentSubmitted,
                        consentStatus?.financialConsentSubmitted,
                        consentStatus?.patientIntakeSubmitted,
                      ].filter(Boolean).length /
                        3) *
                      100
                    }%`,
                    height: "100%",
                    backgroundColor: "#28a745",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Contact Info */}
        {brandingInfo?.branding?.contactInfo && (
          <div
            style={{
              textAlign: "center",
              marginTop: "2rem",
              color: "#666",
              fontSize: "0.9rem",
            }}
          >
            <p>Need help? Contact us:</p>
            <p>
              📞 {brandingInfo.branding.contactInfo.phone} | ✉️{" "}
              {brandingInfo.branding.contactInfo.email}
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      {brandingInfo?.branding?.footerText && (
        <footer
          style={{
            backgroundColor: "#f5f5f5",
            padding: "1rem",
            textAlign: "center",
            color: "#666",
            fontSize: "0.8rem",
            marginTop: "3rem",
          }}
        >
          {brandingInfo.branding.footerText}
        </footer>
      )}
    </div>
  );
};

export default FormSelection;
